import React, { useContext } from "react";
import styled, { keyframes } from "styled-components";
import { renderCount } from "../../../../../../appHelpers/appLoggers";
import { DarkModeContext } from "../../../../../../appContexts/DarkModeContext";
import { GlassCard, SectionRibbon, colors } from "../../../../../ui";

export const Loader_MachineMetadataFC = () => {
  renderCount("MachineMetadataFC");
  const { darkMode } = useContext(DarkModeContext);

  return (
    <GlassCard animated visible={true}>
      <SectionRibbon title="System Information" />

      {/* Main System Info Section */}
      <HeaderSection>
        <LocationSkeleton />
        <RoomSkeleton />
      </HeaderSection>

      <ModernDivider />

      {/* System Details Grid */}
      <MetadataGrid>
        <MetadataItem>
          <MetadataLabel>ID</MetadataLabel>
          <SkeletonBar width="60px" />
        </MetadataItem>

        <MetadataItem>
          <MetadataLabel>SYSTEM</MetadataLabel>
          <SkeletonBar width="90px" />
        </MetadataItem>

        <MetadataItem>
          <MetadataLabel>MODEL</MetadataLabel>
          <SkeletonBar width="100px" />
        </MetadataItem>

        <MetadataItem>
          <MetadataLabel>SERIAL#</MetadataLabel>
          <SkeletonBar width="80px" />
        </MetadataItem>

        <MetadataItem>
          <MetadataLabel>S/V</MetadataLabel>
          <SkeletonBar width="50px" />
        </MetadataItem>
      </MetadataGrid>
    </GlassCard>
  );
};

// Modern Skeleton Loading Animation
const shimmer = keyframes`
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
`;

// Modern Styled Components
const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
`;

const LocationSkeleton = styled.div`
  height: 20px;
  width: 120px;
  background: linear-gradient(
    90deg,
    ${colors.neutral[200]} 25%,
    ${colors.neutral[100]} 37%,
    ${colors.neutral[200]} 63%
  );
  background-size: 400% 100%;
  border-radius: 8px;
  animation: ${shimmer} 1.5s ease-in-out infinite;
`;

const RoomSkeleton = styled.div`
  height: 16px;
  width: 100px;
  background: linear-gradient(
    90deg,
    ${colors.neutral[200]} 25%,
    ${colors.neutral[100]} 37%,
    ${colors.neutral[200]} 63%
  );
  background-size: 400% 100%;
  border-radius: 6px;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  animation-delay: 0.2s;
`;

const ModernDivider = styled.div`
  width: 100%;
  height: 1px;
  background: linear-gradient(
    90deg,
    transparent,
    ${colors.neutral[300]},
    transparent
  );
  margin: 16px 0;
`;

const MetadataGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 16px;
  margin-top: 16px;

  @media screen and (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(100px, 1fr));
    gap: 12px;
  }

  @media screen and (max-width: 480px) {
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
`;

const MetadataItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px;

  @media screen and (max-width: 480px) {
    padding: 6px;
    gap: 6px;
  }
`;

const MetadataLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${colors.neutral[600]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  text-align: center;

  @media screen and (max-width: 768px) {
    font-size: 0.7rem;
  }

  @media screen and (max-width: 480px) {
    font-size: 0.65rem;
  }
`;

const SkeletonBar = styled.div`
  height: 16px;
  width: ${({ width }) => width || "80px"};
  background: linear-gradient(
    90deg,
    ${colors.neutral[200]} 25%,
    ${colors.neutral[100]} 37%,
    ${colors.neutral[200]} 63%
  );
  background-size: 400% 100%;
  border-radius: 8px;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  animation-delay: ${({ delay }) => delay || "0.3s"};

  @media screen and (max-width: 480px) {
    height: 14px;
  }
`;
